import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, NEVER, Observable, of, switchMap, take, takeUntil } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { RootPaths, MonitoringLogger, WindowRef } from '@fcom/core';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { isPresent, mapErrorForMonitoring } from '@fcom/core/utils';
import { DapiHttpResponse } from '@fcom/dapi/api/dapi-http-response';
import { IdAndHash } from '@fcom/dapi/interfaces';
import { BookingAndAppState } from '@fcom/common/interfaces/booking';
import { cartIdAndHash } from '@fcom/booking/store/selectors/cart.selector';
import { finShare } from '@fcom/rx';
import { TestVariant } from '@fcom/common/interfaces';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { initializeCheckoutV2 } from '@fcom/dapi/api/order-functions';

import { mapDeviceForPayment } from '../utils/payment.utils';
import { PaymentActions } from '../store/actions';
import { orderFetchInformation } from '../store/selectors/order.selector';
import { QueueService } from './queue.service';
import { PaymentFlow, PaymentServiceName, PaymentStatus } from '../interfaces';

@Injectable()
export class PaymentService {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingAndAppState>>(Store);
  private monitoringLogger = inject(MonitoringLogger);
  private windowRef = inject(WindowRef);
  private dapiService = inject(DapiService);
  private queueService = inject(QueueService);
  private languageService = inject(LanguageService);

  static ROOT_PATHS = {
    [PaymentFlow.BOOKING]: RootPaths.BOOKING_ROOT,
    [PaymentFlow.MANAGE_BOOKING]: RootPaths.MANAGE_BOOKING_ROOT,
    [PaymentFlow.VOLUNTARY_CHANGE]: RootPaths.VOLUNTARY_CHANGE_ROOT,
    [PaymentFlow.CHECK_IN_FUNNEL]: RootPaths.MANAGE_BOOKING_ROOT + '/check-in/step',
  };

  queuePaymentInit(serviceName: PaymentServiceName, paymentFlow: PaymentFlow, testVariant?: TestVariant): void {
    this.queueService.cancelServiceAvailability$.next();

    const cancelStream$ = this.queueService.toCancelEvent(this.queueService.cancelPayment);
    this.queueService.sendToQueue(
      'paymentInit',
      this.fromOrderCart((idAndHash: IdAndHash) =>
        this.triggerPaymentInit(
          idAndHash.id,
          idAndHash.hash,
          this.languageService.localeValue,
          this.languageService.langValue,
          cancelStream$,
          serviceName,
          paymentFlow,
          idAndHash.orderChangeId,
          testVariant
        )
      ),
      cancelStream$
    );
  }

  /**
   * Triggers payment init. Fetches payment tokenizer url and paymentid
   *
   * @param cartOrOrderId the id of the cart or order
   * @param hash the hash signature of the cart or order
   * @param locale the locale we want the tokenizer form, example: fi_FI
   * @param lang the language to use in urls
   * @param cancelStream$ when this observable emits for the first time, the stream will be cancelled.
   * @param service differentiate which flow was used to trigger the payment
   * @param paymentFlow in which workflow is the payment happening
   * @param orderChangeId the order change id gotten from voluntary change flow
   * @param testVariant the test variant to use for the payment
   */
  public triggerPaymentInit(
    cartOrOrderId: string,
    hash: string,
    locale: string,
    lang: string,
    cancelStream$: Observable<unknown> = NEVER,
    service: PaymentServiceName,
    paymentFlow: PaymentFlow,
    orderChangeId?: string,
    testVariant?: TestVariant
  ): Observable<null> {
    const cartOrOrderIdParam = cartOrOrderId.length === 6 ? 'orderId' : 'cartId';
    const paymentInitData$ = this.dapiService
      .invokeResponse$(initializeCheckoutV2, this.configService.cfg.orderUrl, {
        hash,
        [cartOrOrderIdParam]: cartOrOrderId,
        ...(orderChangeId && { orderChangeId }),
        locale,
        device: mapDeviceForPayment(this.windowRef.nativeWindow.screen.width),
        service,
        ...this.getUrls(paymentFlow, lang),
        testVariant,
      })
      .pipe(
        // eslint-disable-next-line rxjs-x/no-implicit-any-catch
        catchError((errorResponse: HttpErrorResponse) => {
          this.monitoringLogger.error('Error initializing payment', {
            error: mapErrorForMonitoring(errorResponse),
          });
          this.store$.dispatch(PaymentActions.setPaymentStatus({ status: PaymentStatus.TECHNICAL_ERROR }));
          return EMPTY;
        }),
        finShare(),
        takeUntil(cancelStream$)
      );

    paymentInitData$.pipe(take(1)).subscribe({
      next: (response) => {
        this.store$.dispatch(PaymentActions.setPaymentRedirectionUrl({ url: response.headers.get('Location') }));
        // This has to be set to something different than Initialize because of the success/fail redirection, and the root.reducer
        this.store$.dispatch(PaymentActions.setPaymentStatus({ status: PaymentStatus.PENDING_VERIFICATION }));
      },

      error: (error: unknown) => {
        this.monitoringLogger.error('Error in payment init interaction', {
          error: mapErrorForMonitoring(error),
        });
        this.store$.dispatch(PaymentActions.setPaymentStatus({ status: PaymentStatus.TECHNICAL_ERROR }));
      },
    });

    return paymentInitData$.pipe(map((response: DapiHttpResponse<null>) => response.body));
  }

  private getUrls(paymentFlow: PaymentFlow, lang: string): { successUrl: string; failureUrl: string } {
    const urlPrefix = `${document.location.origin}/${lang}`;
    const flowRootPath = PaymentService.ROOT_PATHS[paymentFlow];
    if (paymentFlow === PaymentFlow.CHECK_IN_FUNNEL) {
      return {
        successUrl: `${urlPrefix}/${flowRootPath}/rules/payment/success`,
        failureUrl: `${urlPrefix}/${flowRootPath}/know-your-trip/payment/error`,
      };
    }
    return {
      successUrl: `${urlPrefix}/${flowRootPath}/confirmation`,
      failureUrl: `${urlPrefix}/${flowRootPath}/payment/error`,
    };
  }

  private fromOrderCart = (fn: (idAndHash: IdAndHash) => Observable<unknown>) => {
    const orderIdAndHash$ = this.store$.pipe(orderFetchInformation(), finShare());

    const cartInfo$ = this.store$.pipe(cartIdAndHash(), take(1));

    return orderIdAndHash$.pipe(
      switchMap((idAndHash) => {
        return isPresent(idAndHash) ? of(idAndHash) : cartInfo$;
      }),
      switchMap((a) => fn(a))
    );
  };
}
